import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import boundary from "../data/tunisiaBoundary.json";
import { LISTING_STYLE_URL } from "../config/listingMapStyle";
import { listingMapPoints } from "../utils/listingMapData";
import { configureListingMapWorker } from "../config/listingMapWorker";
import { configureHomeMapRTL } from "../config/homeMapRTL";

export default function HomeTunisiaMap({ listings, href }) {
  const { t } = useTranslation();
  const container = useRef(null), instance = useRef(null);
  const [failed, setFailed] = useState(false);
  const points = useMemo(() => ({ type: "FeatureCollection", features: listingMapPoints(listings)
    .filter(p => p.position[0] >= 30.2 && p.position[0] <= 37.6 && p.position[1] >= 7.5 && p.position[1] <= 11.7)
    .map(p => ({ type: "Feature", geometry: { type: "Point", coordinates: [p.position[1], p.position[0]] }, properties: {} })) }), [listings]);
  const currentPoints = useRef(points); currentPoints.current = points;
  useEffect(() => {
    let map, cancelled = false;
    const initialize = async () => {
    try {
      configureListingMapWorker();
      await configureHomeMapRTL();
      if (cancelled) return;
      map = new maplibregl.Map({ container: container.current, style: LISTING_STYLE_URL,
        center: [9.65, 35.6], zoom: 5.5, minZoom: 5, maxZoom: 10, pitch: 0,
        maxBounds: [[7.3, 30], [12, 38]], scrollZoom: false, dragRotate: false, attributionControl: false });
      instance.current = map;
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
      map.addControl(new maplibregl.AttributionControl({ compact: true, customAttribution: "Boundary: Natural Earth" }));
      map.on("load", () => {
        // Cover neighbouring countries, including their labels; keep the real Tunisian basemap.
        map.addSource("home-country-mask", { type: "geojson", data: { type: "Feature", properties: {}, geometry: {
          type: "Polygon", coordinates: [[[-180,-85],[180,-85],[180,85],[-180,85],[-180,-85]], ...boundary.geometry.coordinates]
        } } });
        map.addLayer({ id: "home-country-mask", type: "fill", source: "home-country-mask", paint: { "fill-color": "#e8f5f7", "fill-opacity": 1 } });
        map.addSource("home-listings", { type: "geojson", data: currentPoints.current });
        // Place points below the mask, so an out-of-country position can never appear.
        map.addLayer({ id: "home-listings", type: "circle", source: "home-listings", paint: {
          "circle-color": "#009c58", "circle-radius": 5, "circle-stroke-width": 2, "circle-stroke-color": "#fff"
        } }, "home-country-mask");
      });
      map.on("error", () => { if (!map.isStyleLoaded()) setFailed(true); });
    } catch { if (!cancelled) setFailed(true); }
    };
    initialize();
    return () => { cancelled = true; instance.current = null; map?.remove(); };
  }, []);
  useEffect(() => { instance.current?.getSource("home-listings")?.setData(points); }, [points]);
  return <section className="desktop-home-map" aria-label={t("desktopHome.mapTitle")}>
    <div className="desktop-home-map-canvas" ref={container} />
    <div className="desktop-home-map-cta"><h2>{t("desktopHome.mapTitle")}</h2>
      {failed && <p role="status">{t("desktopHome.mapUnavailable")}</p>}
      <Link className="lina-action-primary" to={href}>{t("mobile.viewMap")} <span aria-hidden="true">→</span></Link>
    </div>
  </section>;
}
